import restClient from "api/restClient";
import { AxiosResponse } from "axios";
import { Button } from "components/Button/Button";
import { Markdown } from "components/Form/components/Markdown/Markdown";
import type { SchematizedValues } from "components/Form/components/SchemaForm/SchemaForm.types";
import { IconCollection } from "components/Icon/IconCollection";
import ExpandableNotification from "components/Notification/ExpandableNotification";
import NotificationService from "components/Notification/Notification.service";
import RestErrorNotification from "components/Notification/RestErrorNotification";
import RecordLabel from "components/RecordLabel";
import { TimeAgoWithDateTimeFormatter } from "components/TimeAgo/TimeAgoWithDateTimeFormatter";
import { resourceByName } from "config";
import { ActivityConfig, FeConfigType } from "config/config";
import {
    convertTime,
    hasExecutionEndedOrPausedStatus,
    hasExecutionEndedStatus,
} from "config/ts-utils";
import { actions as executionsActions } from "containers/Executions/Executions.reducer";
import { executionsCreateOperationMutation } from "containers/Executions/Executions.type";
import {
    actions as resourceActions,
    actions as resourcesActions,
} from "containers/Resources/Resources.reducer";
import { Action, ActionData } from "containers/Resources/components/CommonActions/Action";
import {
    DeleteListAction,
    PermanentlyDeleteListAction,
    UndeleteListAction,
    deleteActionSignature,
    deletePermanentlyActionSignature,
    exportActionSignature,
} from "containers/Resources/components/CommonActions/CommonActions";
import {
    deleteKey,
    deleteProcessKey,
    restoreKey,
    restoreProcessKey,
} from "containers/Resources/components/CommonActions/constants";
import {
    executionRelatedRecordsContentType,
    commentsTab,
    executionRelatedRecordsTab,
    executionRelatedRecordsTabKey,
    logsTab,
    metadataTab,
} from "containers/Resources/screens/ResourcesSingle/constants";
import { runListActionWithProgress } from "containers/Resources/listActionProgress.utils";
import { Execution } from "graphql/generated/graphql";
import { History } from "history";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { Dispatch } from "redux";
import { getRelativeUrl } from "utils/service";
import { capitalizeFirstLetter, capitalizeFirstLetterRestLowercase } from "utils/utils";
import activity from "./activity";
import identity from "./identity";
import { baseResourceBody, locationField } from "./record";

const recordHref = (resourceName: Parameters<typeof getRelativeUrl>[0], id?: string | null) =>
    id ? getRelativeUrl(resourceName, id) : undefined;

const GoToParentAction: Action = (data, dispatch, history) => {
    const parentId = data.resourceData?.parent_execution_id;
    if (parentId) history.push(getRelativeUrl("execution", parentId));
    return Promise.resolve(null);
};

const GoToCustomObjectAction: Action = (data, dispatch, history) => {
    const customObjectId = data.resourceData?.custom_object_id;
    if (customObjectId) history.push(getRelativeUrl("custom_object", customObjectId));
    return Promise.resolve(null);
};

const OpenFlowAction: Action = (data, dispatch, history) => {
    const flowId = data.resourceData?.flow_id;
    if (flowId) {
        history.push(getRelativeUrl("flow", flowId));
    }
    return Promise.resolve(null);
};

const OpenConnectorAction: Action = (data, dispatch, history) => {
    const connectorId = data.resourceData?.connector_id;
    if (connectorId) history.push(getRelativeUrl("connector", connectorId));
    return Promise.resolve(null);
};

const OpenScheduleAction: Action = (data, dispatch, history) => {
    const scheduleId = data.resourceData?.schedule_id;
    if (scheduleId) history.push(getRelativeUrl("schedule", scheduleId));
    return Promise.resolve(null);
};

const OpenWebhookAction: Action = (data, dispatch, history) => {
    const webhookId = data.resourceData?.webhook_id;
    if (webhookId) history.push(getRelativeUrl("webhook", webhookId));
    return Promise.resolve(null);
};

const OpenMessageAction: Action = (data, dispatch, history) => {
    const messageId = data.resourceData?.message_id;
    if (messageId) history.push(getRelativeUrl("message", messageId));
    return Promise.resolve(null);
};

const OpenWrapperAction: Action = (data, dispatch, history) => {
    const wrapperId = data.resourceData?.wrapper_id;
    if (wrapperId) history.push(getRelativeUrl("wrapper", wrapperId));
    return Promise.resolve(null);
};

const PauseSingleAction: Action = (data, dispatch, history) => {
    return new Promise((resolve, reject) => {
        dispatch({
            type: executionsActions.START_CREATE_EXECUTION_OPERATION,
            promise: { resolve, reject },
            variables: {
                execution_id: data.resourceData?.id,
                operation: {
                    op: "pause",
                },
            },
        });
    });
};

const PauseProcessAction: Action = (data, dispatch, history) => {
    return new Promise((resolve, reject) => {
        restClient
            .patch(`/execution/${data.resourceData?.id}/process/pause`)
            .then(({ data }) => {
                resolve(data);
                NotificationService.success(
                    data === 1
                        ? `${data} execution was paused.`
                        : `${data} executions were paused.`,
                );
            })
            .catch((error) => {
                NotificationService.error(<RestErrorNotification error={error} />);
                reject(error);
            });
    });
};

const ResumeSingleAction: Action = (data, dispatch, history) => {
    return new Promise((resolve, reject) => {
        dispatch({
            type: executionsActions.START_CREATE_EXECUTION_OPERATION,
            promise: { resolve, reject },
            variables: {
                execution_id: data.resourceData?.id,
                operation: {
                    op: "run",
                },
            },
        });
    });
};

const ReRunExecutionAction: Action = (data, dispatch, history) => {
    return new Promise((resolve, reject) => {
        restClient
            .patch(`/execution/${data.resourceData?.id}/restart`)
            .then(({ data: responseData }) => {
                resolve(responseData);
            })
            .catch((error) => {
                NotificationService.error(<RestErrorNotification error={error} />);
                reject(error);
            });
    });
};

const ResumeProcessAction: Action = (data, dispatch, history) => {
    return new Promise((resolve, reject) => {
        restClient
            .patch(`/execution/${data.resourceData?.id}/process/resume`)
            .then(({ data }) => {
                resolve(data);
                NotificationService.success(
                    data === 1
                        ? `${data} execution was resumed.`
                        : `${data} executions were resumed.`,
                );
            })
            .catch((error) => {
                NotificationService.error(<RestErrorNotification error={error} />);
                reject(error);
            });
    });
};

const CancelSingleAction: Action = (data, dispatch, history) => {
    return new Promise((resolve, reject) => {
        dispatch({
            type: executionsActions.START_CREATE_EXECUTION_OPERATION,
            promise: { resolve, reject },
            variables: {
                execution_id: data.resourceData?.id,
                operation: {
                    op: "cancel",
                },
            },
        });
    });
};

const StepSingleAction: Action = (data, dispatch, history) => {
    return new Promise((resolve, reject) => {
        dispatch({
            type: executionsActions.START_CREATE_EXECUTION_OPERATION,
            promise: { resolve, reject },
            variables: {
                execution_id: data.resourceData?.id,
                operation: {
                    op: "step",
                },
            },
        });
    });
};

const RemoveAllBreakpointsAction: Action = (data, dispatch, history) => {
    return new Promise((resolve, reject) => {
        dispatch({
            type: resourceActions.START_UPDATE_RESOURCE,
            promise: { resolve, reject },
            resourceName: "execution",
            variables: {
                id: data.resourceData?.id,
                breakpoints: [],
            },
        });
    }).then(() => {
        NotificationService.success("All breakpoints removed from execution and its parents");
    });
};

const CancelProcessAction: Action = (data, dispatch, history) => {
    return new Promise((resolve, reject) => {
        restClient
            .patch(`/execution/${data.resourceData?.id}/process/cancel`)
            .then(({ data }) => {
                resolve(data);
                NotificationService.success(
                    data === 1
                        ? `${data} execution was cancelled.`
                        : `${data} executions were cancelled.`,
                );
            })
            .catch((error) => {
                NotificationService.error(<RestErrorNotification error={error} />);
                reject(error);
            });
    });
};

const IgnoreScheduledSingleAction: Action = (data, dispatch, history) => {
    return new Promise((resolve, reject) => {
        dispatch({
            type: executionsActions.START_CREATE_EXECUTION_OPERATION,
            promise: { resolve, reject },
            variables: {
                execution_id: data.resourceData?.id,
                operation: {
                    op: "ignore_scheduled",
                },
            },
        });
    });
};

const IgnoreDependencySingleAction: Action = (data, dispatch, history) => {
    return new Promise((resolve, reject) => {
        dispatch({
            type: executionsActions.START_CREATE_EXECUTION_OPERATION,
            promise: { resolve, reject },
            variables: {
                execution_id: data.resourceData?.id,
                operation: {
                    op: "ignore_dependency",
                },
            },
        });
    });
};

const IgnoreLockSingleAction: Action = (data, dispatch, history) => {
    return new Promise((resolve, reject) => {
        dispatch({
            type: executionsActions.START_CREATE_EXECUTION_OPERATION,
            promise: { resolve, reject },
            variables: {
                execution_id: data.resourceData?.id,
                operation: {
                    op: "ignore_lock",
                },
            },
        });
    });
};

interface AsyncProcessActionProps {
    data: ActionData;
    history: History;
    notificationText: string;
    additionalParameters?: Record<string, any>;
    dispatch: Dispatch;
    actionType: string;
    navigate?: boolean;
}

const AsyncProcessAction = ({
    data,
    history,
    notificationText,
    additionalParameters = {},
    dispatch,
    actionType,
    navigate = true,
}: AsyncProcessActionProps) => {
    return new Promise((resolve, reject) => {
        dispatch({
            type: actionType,
            promise: { resolve, reject },
            resourceName: data.resourceType,
            id: data.resourceData?.id,
            parameters: {
                ...additionalParameters,
                wait: false,
                recursive: true,
            },
        });
    })
        .then(() => {
            const notificationUpperCased = capitalizeFirstLetter(notificationText);
            NotificationService.success(
                `${notificationUpperCased} of the process "${data.resourceData?.name}" started`,
            );
            if (navigate !== false) {
                history.push("/");
            }
        })
        .catch((error) => {
            NotificationService.error(<RestErrorNotification error={error} />);
        });
};

const DeleteProcessAction: Action = (data, dispatch, history) =>
    AsyncProcessAction({
        data,
        history,
        notificationText: "move to trash",
        dispatch,
        actionType: resourcesActions.START_DELETE_RESOURCE,
        additionalParameters: { permanently: false },
        navigate: false,
    });
const DeletePermanentlyProcessAction: Action = (data, dispatch, history) =>
    AsyncProcessAction({
        data,
        history,
        notificationText: "permanent deletion",
        dispatch,
        actionType: resourcesActions.START_PERMANENTLY_DELETE_RESOURCE,
        additionalParameters: { permanently: true },
    });
const RestoreProcessAction: Action = (data, dispatch, history) =>
    AsyncProcessAction({
        data,
        history,
        notificationText: "restore",
        dispatch,
        actionType: resourcesActions.START_UNDELETE_RESOURCE,
        navigate: false,
    });

interface ExecutionActionData {
    id: string;
    name: string;
    resourceData: Execution;
}

interface GeneralActionProps {
    resources: ExecutionActionData[];
    apiCall: (execution: ExecutionActionData) => Promise<AxiosResponse<any>>;
    omitWhen?: (execution: Execution) => boolean;
    icon: React.ReactNode;
    notificationText: string;
    label: string;
    afterAction: () => void;
    executionType: "execution" | "process";
    isDelete?: boolean;
}

const GeneralListAction = ({
    resources,
    apiCall,
    omitWhen,
    icon,
    notificationText,
    label,
    afterAction,
    executionType,
    isDelete,
}: GeneralActionProps) => {
    const [isLoading, setLoading] = useState(false);
    const dispatch = useDispatch();

    const canSkipAllResources = resources.every((resource) =>
        omitWhen ? omitWhen(resource.resourceData) : false,
    );
    const progressLabel = isDelete
        ? notificationText.includes("permanently")
            ? "Deleting permanently"
            : "Moving to trash"
        : label;

    const action = async (executions: ExecutionActionData[]) => {
        let successfulExecutions = 0;
        let successfulProcesses = 0;
        const failedResult: any = [];
        let skippedResources = 0;
        const applicableExecutions = executions.filter(
            (execution) => !omitWhen?.(execution.resourceData),
        );

        setLoading(true);

        try {
            const results = await runListActionWithProgress(
                dispatch,
                applicableExecutions,
                progressLabel,
                async (execution) => {
                    try {
                        const success = await apiCall(execution);
                        return { execution, success, error: null };
                    } catch (error: any) {
                        return {
                            execution,
                            success: null,
                            error: error?.message ?? error,
                        };
                    }
                },
            );

            for (const result of results) {
                if (result.error) {
                    failedResult.push({
                        execution: result.execution.resourceData,
                        error: result.error,
                    });
                } else {
                    successfulExecutions +=
                        (typeof result.success?.data === "number" ? result.success?.data : 1) ?? 0;
                    successfulProcesses +=
                        typeof result.success?.data === "number" && executionType === "process"
                            ? 1
                            : 0;
                }
            }

            skippedResources = executions.length - applicableExecutions.length;
        } finally {
            setLoading(false);
            afterAction();
        }
        const plural = executionType === "execution" ? "s" : "es";
        if (skippedResources || successfulExecutions) {
            const getSuccessfulMessage = () => {
                if (!successfulExecutions) {
                    return "";
                } else if (isDelete) {
                    const action = notificationText.includes("permanently")
                        ? "Permanent deletion"
                        : "Move to trash";
                    return `${action} of ${
                        executionType === "process"
                            ? `${successfulProcesses} process${
                                  successfulProcesses > 1 ? "es" : ""
                              } consisting of `
                            : ""
                    }${successfulExecutions} execution${
                        successfulExecutions > 1 ? "s" : ""
                    } successfully started.`;
                } else {
                    return `${
                        executionType === "process"
                            ? `${successfulProcesses} process${
                                  successfulProcesses > 1 ? "es" : ""
                              } consisting of `
                            : ""
                    }${successfulExecutions} execution${
                        successfulExecutions > 1 ? "s" : ""
                    } ${notificationText} successfully.`;
                }
            };

            if (skippedResources) {
                const skippedMessage = `${skippedResources} ${executionType}${
                    skippedResources > 1 ? plural : ""
                } could not be ${notificationText}.`;
                NotificationService.info(`${getSuccessfulMessage()} ${skippedMessage}`);
            } else {
                NotificationService.success(getSuccessfulMessage());
            }
        }
        if (failedResult.length) {
            NotificationService.error(
                <ExpandableNotification
                    title={`${failedResult.length} ${executionType}${
                        failedResult.length === 1 ? "" : plural
                    } could not be ${notificationText}`}
                    details={
                        <ul>
                            {failedResult.map(({ execution, error }) => (
                                <li key={execution?.id}>
                                    {execution?.name}:{" "}
                                    {
                                        <Markdown
                                            markdownString={error}
                                            className="notificationMarkdown"
                                        />
                                    }
                                </li>
                            ))}
                        </ul>
                    }
                    detailsForClipboard={JSON.stringify(
                        failedResult.map(({ error, execution }: any) => ({
                            [execution?.name]: error,
                        })),
                    )}
                />,
            );
        }
    };

    return (
        <Button
            onClick={() => {
                action(resources);
            }}
            loading={isLoading}
            disabled={!resources?.length || canSkipAllResources}
            icon={icon}
            variation="dark"
            label={label}
        />
    );
};

interface SingleExecutionActionProps extends Omit<
    GeneralActionProps,
    "label" | "apiCall" | "executionType" | "isDelete"
> {
    operation: "delete" | "resume" | "pause" | "cancel";
    additionalParameter?: string;
    buttonLabel?: string;
}

const SingleExecutionListAction = ({
    resources,
    operation,
    omitWhen,
    icon,
    notificationText,
    afterAction,
}: SingleExecutionActionProps) => {
    return GeneralListAction({
        resources,
        afterAction,
        apiCall: (data) =>
            restClient.post(`/graphql`, {
                query: executionsCreateOperationMutation,
                variables: {
                    execution_id: data.resourceData?.id,
                    operation: {
                        op: operation,
                    },
                },
            }),
        omitWhen,
        icon,
        notificationText,
        label: `${capitalizeFirstLetter(operation)} single execution`,
        executionType: "execution",
    });
};

export const PauseExecutionListAction = ({ resources, afterAction }: any) => {
    return SingleExecutionListAction({
        resources,
        operation: "pause",
        omitWhen: (data) => hasExecutionEndedStatus(data?.status) || data?.status === "PAUSED",
        icon: IconCollection.pause,
        notificationText: "paused",
        afterAction,
    });
};

export const CancelExecutionListAction = ({ resources, afterAction }: any) => {
    return SingleExecutionListAction({
        resources,
        operation: "cancel",
        omitWhen: (data) => hasExecutionEndedStatus(data?.status),
        icon: IconCollection.close,
        notificationText: "canceled",
        afterAction,
    });
};

export const ResumeExecutionListAction = ({ resources, afterAction }: any) => {
    return SingleExecutionListAction({
        resources,
        operation: "resume",
        omitWhen: (data) => data?.status !== "PAUSED",
        icon: IconCollection.play,
        notificationText: "resumed",
        afterAction,
    });
};

const ProcessListAction = ({
    resources,
    operation,
    omitWhen,
    icon,
    notificationText,
    additionalParameter,
    buttonLabel,
    afterAction,
}: SingleExecutionActionProps) => {
    return GeneralListAction({
        resources,
        afterAction,
        apiCall: (execution) =>
            restClient.patch(
                `/execution/${execution.resourceData?.id}/process/${operation}${
                    additionalParameter ? `?${additionalParameter}` : ""
                }`,
            ),
        omitWhen,
        icon,
        notificationText,
        label: buttonLabel ?? `${capitalizeFirstLetter(operation)} process`,
        executionType: "process",
        isDelete: operation === "delete",
    });
};

const DeleteProcessListAction = ({ resources, afterAction }: any) =>
    DeleteListAction({
        resources,
        afterAction,
        label: "Move process to trash",
        parameters: {
            recursive: true,
            wait: false,
        },
    });

const PermanentlyDeleteProcessListAction = ({ resources, afterAction }: any) =>
    PermanentlyDeleteListAction({
        resources,
        afterAction,
        label: "Delete process permanently",
        parameters: {
            recursive: true,
            wait: false,
        },
    });

const RestoreProcessListAction = ({ resources, afterAction }: any) =>
    UndeleteListAction({
        resources,
        afterAction,
        label: "Restore process",
        parameters: {
            recursive: true,
            wait: false,
        },
    });

const PauseProcessListAction = ({ resources, afterAction }: any) =>
    ProcessListAction({
        resources,
        afterAction,
        operation: "pause",
        omitWhen: (execution) =>
            hasExecutionEndedStatus(execution?.status) ||
            execution?.status === "PAUSED" ||
            execution?.deleted_at !== null,
        icon: IconCollection.pause,
        notificationText: "paused",
    });

const CancelProcessListAction = ({ resources, afterAction }: any) =>
    ProcessListAction({
        resources,
        afterAction,
        operation: "cancel",
        omitWhen: (execution) =>
            hasExecutionEndedStatus(execution?.status) || execution?.deleted_at !== null,
        icon: IconCollection.close,
        notificationText: "canceled",
    });

const ResumeProcessListAction = ({ resources, afterAction }: any) =>
    ProcessListAction({
        resources,
        afterAction,
        operation: "resume",
        omitWhen: (execution) => execution?.status !== "PAUSED" || execution?.deleted_at !== null,
        icon: IconCollection.play,
        notificationText: "resumed",
    });

const SwitchToProductiveAction: Action = (data, dispatch, history) => {
    return new Promise((resolve, reject) => {
        dispatch({
            type: resourceActions.START_UPDATE_RESOURCE,
            promise: { resolve, reject },
            resourceName: "execution",
            variables: {
                id: data.resourceData?.id,
                is_productive: true,
                use_breakpoints: false,
                use_only_explicit_breakpoints: false,
            },
        });
    });
};

const RemoveSavepointsDataAction: Action = (data, dispatch, history) => {
    return new Promise((resolve, reject) => {
        dispatch({
            type: executionsActions.START_CREATE_EXECUTION_OPERATION,
            promise: { resolve, reject },
            variables: {
                execution_id: data.resourceData?.id,
                operation: {
                    op: "remove_savepoints",
                },
            },
        });
    }).then(() => {
        NotificationService.success("All savepoints have been removed from the execution.");
    });
};

const executionTypeRecord = (["connector", "flow", "schedule", "wrapper"] as FeConfigType[]).map(
    (type) => {
        const typeIdStr = `${type}_id`;
        const typeIdTypeStr = `${typeIdStr}_${type}`;
        return {
            label: capitalizeFirstLetterRestLowercase(type),
            valueKey: "",
            dto: (execution: Execution) =>
                execution?.[typeIdStr] ? (
                    <RecordLabel
                        link={getRelativeUrl(type, execution[typeIdStr])}
                        name={execution[typeIdTypeStr]?.["name"]}
                        icon={resourceByName[type]["icon"]}
                    />
                ) : null,
        };
    },
);

export const outputTabKey = "output";
export const inputTabKey = "input";
export const scriptTabKey = "script";

const resourceName = "execution";

const config: ActivityConfig = {
    label: "Execution",
    icon: IconCollection.flashOutline,
    resourceName,
    resourceClass: "activity",
    defaultActiveTab: outputTabKey,
    ignoreIsReadonly: true,
    layout: [
        {
            partial: "pageHeaderForm",
            config: {
                editDescription: false,
                editName: false,
            },
        },
        { partial: "ExecutionSingleStatus" },
        {
            partial: "tabs",
            config: {
                tabs: [
                    { uid: outputTabKey, label: "Output" },
                    { uid: inputTabKey, label: "Input" },
                    { uid: scriptTabKey, label: "Script" },
                    { uid: "children", label: "Children" },
                    logsTab.tab,
                    { uid: "savepoints", label: "Savepoints" },
                    executionRelatedRecordsTab.tab,
                ],
                hiddenTabs: [
                    metadataTab.tab,
                    commentsTab.tab,
                    { uid: "output_schema", label: "Output schema" },
                    { uid: "input_schema", label: "Input schema" },
                ],
                content: [
                    logsTab.content,
                    {
                        uid: outputTabKey,
                        type: "schemaForm",
                        valueGetter: (recordData: any): SchematizedValues => {
                            return {
                                values: recordData.output_value,
                                schema: recordData.output_schema,
                            };
                        },
                        fieldProps: {
                            isReadOnly: true,
                        },
                    },
                    {
                        uid: inputTabKey,
                        type: "schemaForm",
                        valueGetter: (recordData: any): SchematizedValues => {
                            return {
                                values: recordData.input_value,
                                schema: recordData.input_schema,
                            };
                        },
                        fieldProps: {
                            isReadOnly: true,
                        },
                    },
                    {
                        uid: "output_schema",
                        type: "readonlySchemaForm",
                        value: "output_schema",
                    },
                    {
                        uid: "input_schema",
                        type: "readonlySchemaForm",
                        value: "input_schema",
                    },
                    {
                        uid: "script",
                        type: "codeEditor",
                        value: "script",
                        fieldProps: {
                            lang: "python",
                            isReadOnly: true,
                            breakpointsEnabled: true,
                        },
                    },
                    {
                        uid: "children",
                        type: "executionsListing",
                    },
                    {
                        uid: "savepoints",
                        type: "savepointsListing",
                    },
                    {
                        uid: executionRelatedRecordsTabKey,
                        type: executionRelatedRecordsContentType,
                    },
                    metadataTab.content,
                    commentsTab.content,
                ],
            },
        },
    ],
    actions: {
        shown: [
            {
                key: "goToParent",
                action: GoToParentAction,
                icon: () => IconCollection.arrowUpLeft,
                label: () => "Go to parent",
                href: (data: ActionData) =>
                    recordHref("execution", data.resourceData?.parent_execution_id),
                omitWhen: (data: ActionData) => !data.resourceData?.parent_execution_id,
            },
            {
                key: "goToCustomObject",
                action: GoToCustomObjectAction,
                icon: () => IconCollection.arrowUpLeft,
                label: () => "Go to custom object",
                href: (data: ActionData) =>
                    recordHref("custom_object", data.resourceData?.custom_object_id),
                omitWhen: (data: ActionData) => !data.resourceData?.custom_object_id,
            },
            {
                key: "openFlow",
                action: OpenFlowAction,
                icon: () => IconCollection.codeTags,
                label: () => "Open flow",
                href: (data: ActionData) => recordHref("flow", data.resourceData?.flow_id),
                omitWhen: (data: ActionData) => !data.resourceData?.flow_id,
            },
            {
                key: "openConnector",
                action: OpenConnectorAction,
                icon: () => IconCollection.connection,
                label: () => "Open connector",
                href: (data: ActionData) =>
                    recordHref("connector", data.resourceData?.connector_id),
                omitWhen: (data: ActionData) => !data.resourceData?.connector_id,
            },
            {
                key: "openSchedule",
                action: OpenScheduleAction,
                icon: () => IconCollection.timetable,
                label: () => "Open schedule",
                href: (data: ActionData) => recordHref("schedule", data.resourceData?.schedule_id),
                omitWhen: (data: ActionData) => !data.resourceData?.schedule_id,
            },
            {
                key: "openWebhook",
                action: OpenWebhookAction,
                icon: () => IconCollection.webhook,
                label: () => "Open webhook",
                href: (data: ActionData) => recordHref("webhook", data.resourceData?.webhook_id),
                omitWhen: (data: ActionData) => !data.resourceData?.webhook_id,
            },
            {
                key: "openMessage",
                action: OpenMessageAction,
                icon: () => IconCollection.bellOutline,
                label: () => "Open message",
                href: (data: ActionData) => recordHref("message", data.resourceData?.message_id),
                omitWhen: (data: ActionData) => !data.resourceData?.message_id,
            },
            {
                key: "openWrapper",
                action: OpenWrapperAction,
                icon: () => IconCollection.codeBraces,
                label: () => "Open wrapper",
                href: (data: ActionData) => recordHref("wrapper", data.resourceData?.wrapper_id),
                omitWhen: (data: ActionData) => !data.resourceData?.wrapper_id,
            },
            {
                key: "pauseProcess",
                action: PauseProcessAction,
                icon: () => IconCollection.pause,
                label: () => "Pause process",
                omitWhen: (data: ActionData) =>
                    hasExecutionEndedStatus(data.resourceData?.status) ||
                    data.resourceData?.status === "PAUSED" ||
                    data.resourceData?.deleted_at,
            },
            {
                key: "resumeProcess",
                action: ResumeProcessAction,
                icon: () => IconCollection.play,
                label: () => "Resume process",
                omitWhen: (data: ActionData) =>
                    data.resourceData?.status !== "PAUSED" || data.resourceData?.deleted_at,
            },
            {
                key: "stepProcess",
                action: StepSingleAction,
                icon: () => IconCollection.debugStepOver,
                label: () => "Step into",
                omitWhen: (data: ActionData) =>
                    data.resourceData?.status !== "PAUSED" ||
                    !data.resourceData?.use_breakpoints ||
                    data.resourceData?.deleted_at ||
                    data.resourceData?.use_only_explicit_breakpoints,
            },
            {
                key: "removeAllBreakpoints",
                action: RemoveAllBreakpointsAction,
                icon: () => IconCollection.circleOffOutline,
                label: () => "Remove all breakpoints",
                omitWhen: (data: ActionData) =>
                    !data.resourceData?.use_breakpoints ||
                    data.resourceData?.use_only_explicit_breakpoints ||
                    data.resourceData?.deleted_at ||
                    data.resourceData?.breakpoints?.length === 0,
            },
            {
                key: "cancelProcess",
                action: CancelProcessAction,
                icon: () => IconCollection.close,
                label: () => "Cancel process",
                omitWhen: (data: ActionData) =>
                    hasExecutionEndedStatus(data.resourceData?.status) ||
                    data.resourceData?.deleted_at,
            },
            {
                key: "ignoreScheduledSingle",
                action: IgnoreScheduledSingleAction,
                icon: () => IconCollection.skipNext,
                label: () => "Ignore Scheduled",
                omitWhen: (data: ActionData) =>
                    data.resourceData?.status !== "SCHEDULED" || data.resourceData?.deleted_at,
            },
            {
                key: "ignoreDependencySingle",
                action: IgnoreDependencySingleAction,
                icon: () => IconCollection.skipNext,
                label: () => "Ignore Dependency",
                omitWhen: (data: ActionData) =>
                    data.resourceData?.status !== "WAITING_DEPENDENCY" ||
                    data.resourceData?.deleted_at,
            },
            {
                key: "ignoreLockSingle",
                action: IgnoreLockSingleAction,
                icon: () => IconCollection.skipNext,
                label: () => "Ignore Lock",
                omitWhen: (data: ActionData) =>
                    data.resourceData?.status !== "WAITING_LOCK" || data.resourceData?.deleted_at,
            },
            {
                key: "switchToProductive",
                action: SwitchToProductiveAction,
                icon: () => IconCollection.playBoxMultiple,
                label: () => "Switch to productive",
                omitWhen: (data: ActionData) =>
                    data.resourceData?.is_productive || data.resourceData?.deleted_at,
            },
            {
                key: "reRun",
                action: ReRunExecutionAction,
                icon: () => IconCollection.refresh,
                label: () => "Re-run execution",
                omitWhen: (data: ActionData) =>
                    !hasExecutionEndedOrPausedStatus(data.resourceData?.status) ||
                    data.resourceData?.deleted_at,
            },
        ],
        hidden: [
            {
                key: "pauseSingle",
                action: PauseSingleAction,
                icon: () => IconCollection.pause,
                label: () => "Pause single execution",
                omitWhen: (data: ActionData) =>
                    hasExecutionEndedStatus(data.resourceData?.status) ||
                    data.resourceData?.status === "PAUSED",
            },
            {
                key: "resumeSingle",
                action: ResumeSingleAction,
                icon: () => IconCollection.play,
                label: () => "Resume single execution",
                omitWhen: (data: ActionData) => data.resourceData?.status !== "PAUSED",
            },
            {
                key: "cancelSingle",
                action: CancelSingleAction,
                icon: () => IconCollection.close,
                label: () => "Cancel single execution",
                omitWhen: (data: ActionData) => hasExecutionEndedStatus(data.resourceData?.status),
            },
            exportActionSignature,
            deleteActionSignature,
            deletePermanentlyActionSignature,
            {
                key: "removeSavepoints",
                action: RemoveSavepointsDataAction,
                label: () => "Remove savepoints",
                icon: () => IconCollection.deleteSweepOutline,
            },
            {
                key: "deleteProcess",
                action: DeleteProcessAction,
                icon: () => IconCollection.deleteSweepOutline,
                label: () => "Move process to trash",
            },
            {
                key: "restoreProcess",
                action: RestoreProcessAction,
                icon: () => IconCollection.deleteRestore,
                label: () => "Restore process",
            },
            {
                key: "deleteProcessPermanently",
                action: DeletePermanentlyProcessAction,
                icon: () => IconCollection.deleteAlertOutline,
                label: () => "Delete process permanently",
            },
        ],
    },
    listActions: {
        shown: [],
        hidden: [
            {
                key: restoreKey,
                action: (props) =>
                    UndeleteListAction({ ...props, label: "Restore single execution" }),
            },
            {
                key: restoreProcessKey,
                action: RestoreProcessListAction,
            },
            {
                key: deleteKey,
                action: (props) =>
                    DeleteListAction({ ...props, label: "Move single execution to trash" }),
            },
            { key: deleteProcessKey, action: DeleteProcessListAction },
            {
                key: "deletePermanently",
                action: (props) =>
                    PermanentlyDeleteListAction({
                        ...props,
                        label: "Delete single execution permanently",
                    }),
            },
            {
                key: "deleteProcessPermanently",
                action: PermanentlyDeleteProcessListAction,
            },
        ],
        allLevel: [
            { key: "pauseExecution", action: PauseExecutionListAction },
            { key: "cancelExecution", action: CancelExecutionListAction },
            { key: "resumeExecution", action: ResumeExecutionListAction },
        ],
        topLevel: [
            { key: "pauseProcess", action: PauseProcessListAction },
            { key: "cancelProcess", action: CancelProcessListAction },
            { key: "resumeProcess", action: ResumeProcessListAction },
        ],
    },
    // TODO: send graphql query as POST payload
    graphql: {
        resource: resourceName,
        resourceBody: `
            ${activity.graphql.resourceBody}
            ${identity.graphql.resourceBody}
            process_id
            type
            status
            end_status
            is_paused
            started_at
            ended_at
            scheduled_for
            schedule_id
            schedule_id_schedule {
                name
            }
            webhook_id
            parent_execution_id
            custom_object_id
            input_value_size_bytes
            output_value_size_bytes
            input_schema
            output_schema
            message
            dependency_id
            dependency_id_dependency {
                return_when
                dependency_execution_list_dependency_id(limit:0, order: "+id") {
                    dependency_execution_count
                }
            }
            setting_id
            setting_id_setting {
                name
            }
            message_id
            message_id_message {
                subject
                body
                is_submitted
            }
            is_productive
            flow_id
            flow_id_flow {
                name
            }
            wrapper_id
            wrapper_id_wrapper {
                name
            }
            breakpoints
            script
            connector_type
            connector_id
            connector_id_connector{
                name
                id
            }
            sync_config_id
            sync_config_id_identity{
                name
            }
            is_remote_processed
            last_activity
            breakpoints
            last_command
            lineno
            savepoint_retention
            use_breakpoints
            use_only_explicit_breakpoints
            priority
            max_runtime_minutes
        `,
        resourceBodyListing: `
            ${baseResourceBody}
            activity_type
            status
            message
            type
            started_at
            dependency_id
            dependency_id_dependency {
                return_when
                dependency_execution_list_dependency_id(limit:0, order: "+id") {
                    dependency_execution_count
                }
            }
            setting_id
            setting_id_setting {
                name
            }
            message_id
            message_id_message {
                subject
                body
                is_submitted
            }
            flow_id
            flow_id_flow {
                name
            }
            scheduled_for
            connector_type
            connector_id
            connector_id_connector{
                name
            }
            schedule_id
            schedule_id_schedule {
                name
            }
            webhook_id
            wrapper_id
            wrapper_id_wrapper {
                name
            }
            parent_execution_id
            custom_object_id
            message_id
            is_productive
            flow_id
            connector_type
            connector_id
            connector_type
            connector_id_connector{
                name
            }
            sync_config_id
            sync_config_id_identity{
                name
            }
            is_remote_processed
            last_command
            lineno
            use_breakpoints
            use_only_explicit_breakpoints
            priority
        `,
        updateVariables: `
            ${activity.graphql.updateVariables(resourceName)}
            $is_productive: Boolean,
            $breakpoints: [Int],
            $use_breakpoints: Boolean,
            $use_only_explicit_breakpoints: Boolean,
            $priority: Int
            $max_runtime_minutes: Int
        `,
        updateQuery: `, 
            ${activity.graphql.updateQuery(resourceName)}
            is_productive: $is_productive,
            breakpoints: $breakpoints,
            use_breakpoints: $use_breakpoints,
            use_only_explicit_breakpoints: $use_only_explicit_breakpoints,
            priority: $priority
            max_runtime_minutes: $max_runtime_minutes
        `,
        createVariables: `
            $type: ExecutionType!,
            $is_productive: Boolean,
            $flow_id: String,
            $input_value: SchemalessData,
            $unwrapped: Boolean,
            $scheduled_for: String,
            $remote_workspace_name: String,
            $roles: SchemalessData,
            ${locationField(resourceName).variables}
            $use_breakpoints: Boolean,
            $use_only_explicit_breakpoints: Boolean,
            $priority: Int,
            $max_runtime_minutes: Int,
        `,
        createQuery: `
            type: $type,
            is_productive: $is_productive,
            flow_id: $flow_id,
            input_value: $input_value,
            unwrapped: $unwrapped,
            scheduled_for: $scheduled_for,
            remote_workspace_name: $remote_workspace_name,
            ${locationField(resourceName).query}
            roles: $roles,
            use_breakpoints: $use_breakpoints,
            use_only_explicit_breakpoints: $use_only_explicit_breakpoints,
            priority: $priority,
            max_runtime_minutes: $max_runtime_minutes,
        `,
    },
    hideCreateNew: true,
    detailsSidebar: [
        {
            label: "Last activity",
            valueKey: "last_activity",
            dto: (value: any) => (value ? <TimeAgoWithDateTimeFormatter datetime={value} /> : null),
        },
        {
            label: "Started at",
            valueKey: "started_at",
            dto: (value) => (value ? <TimeAgoWithDateTimeFormatter datetime={value} /> : null),
        },
        {
            label: "Ended at",
            valueKey: "ended_at",
            dto: (value) => (value ? <TimeAgoWithDateTimeFormatter datetime={value} /> : null),
        },
        {
            label: "Runtime",
            valueKey: "",
            dto: (execution: Execution) => {
                if (execution?.ended_at && execution?.started_at) {
                    return convertTime(
                        (Date.parse(execution.ended_at) - Date.parse(execution.started_at)) / 1000,
                    );
                }
                if (execution?.started_at) {
                    // TODO: use a updating "duration" component (similar to TimeAgo, but without the "ago" part)
                    return convertTime((Date.now() - Date.parse(execution.started_at)) / 1000);
                }
                return null;
            },
        },
        {
            label: "Savepoint retention",
            valueKey: "",
            dto: (execution: Execution) => execution?.savepoint_retention ?? null,
        },
        ...executionTypeRecord,
        {
            label: "Remote execution",
            valueKey: "",
            dto: (execution: Execution) => {
                if (!execution?.sync_config_id) return null;
                const sync_config_link = (
                    <RecordLabel
                        link={getRelativeUrl("sync_config", execution?.sync_config_id)}
                        name={execution?.sync_config_id_identity?.name}
                        icon={resourceByName.sync_config.icon}
                    />
                );
                if (execution?.is_remote_processed) {
                    return <>Running remotely in {sync_config_link}</>;
                }
                return <>Remotely started by {sync_config_link}</>;
            },
        },
    ],
};
export default config;
